// src/api/vendor.ts
import type { Address, ApiResponse, ReturnRequest } from '../types';
import { apiClient } from './client';

export const vendorAPI = {

    getDashboardStats: async () => {
        try {
            const response = await apiClient.get('/vendors/dashboard/');
            return { data: response.data, error: null };
        } catch (error: any) {
            return {
                data: null,
                error: error.response?.data?.detail || 'Failed to fetch dashboard data'
            };
        }
    },

    submitApplication: async (data: any) => {
        const formData = new FormData();

        // Append all fields
        formData.append('business_name', data.business_name);
        formData.append('business_email', data.business_email);
        formData.append('business_phone', data.business_phone);
        formData.append('vendor_type', data.vendor_type);
        formData.append('business_address', JSON.stringify(data.business_address));

        if (data.website) formData.append('website', data.website);
        if (data.description) formData.append('description', data.description);
        if (data.registration_number) formData.append('registration_number', data.registration_number);
        if (data.tax_id) formData.append('tax_id', data.tax_id);

        if (data.registration_file) {
            formData.append('registration_file', data.registration_file);
        }
        if (data.address_proof) {
            formData.append('address_proof', data.address_proof);
        }

        try {
            const response = await apiClient.post('/vendors/apply/', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            return { data: response.data, error: null };
        } catch (error: any) {
            // Extract detailed error messages
            let errorMessage = 'Failed to submit application';

            if (error.response?.data) {
                const errorData = error.response.data;

                // Handle field-specific errors
                if (typeof errorData === 'object') {
                    const messages: string[] = [];

                    Object.entries(errorData).forEach(([field, value]) => {
                        if (Array.isArray(value)) {
                            messages.push(`${field}: ${value.join(', ')}`);
                        } else if (typeof value === 'string') {
                            messages.push(`${field}: ${value}`);
                        } else if (typeof value === 'object') {
                            // Handle nested errors
                            Object.entries(value).forEach(([subField, subValue]) => {
                                if (Array.isArray(subValue)) {
                                    messages.push(`${field}.${subField}: ${subValue.join(', ')}`);
                                }
                            });
                        }
                    });

                    if (messages.length > 0) {
                        errorMessage = messages.join('; ');
                    } else if (errorData.error) {
                        errorMessage = errorData.error;
                    } else if (errorData.message) {
                        errorMessage = errorData.message;
                    }
                } else if (typeof errorData === 'string') {
                    errorMessage = errorData;
                }
            } else if (error.message) {
                errorMessage = error.message;
            }

            return { data: null, error: errorMessage };
        }
    },

    getApplicationStatus: async () => {
        try {
            const response = await apiClient.get('/vendors/application-status/');
            return { data: response.data, error: null };
        } catch (error: any) {
            return {
                data: null,
                error: error.response?.data?.detail || 'Failed to fetch status'
            };
        }
    },

    getVendorProfile: async () => {
        const response = await apiClient.get('/vendors/profile/');
        return response.data;
    },

    // Start sale on product
    startProductSale: (productId: number, data: { discount_percent: number; sale_start: string; sale_expiry: string }) => {
        return apiClient.post(`/vendors/products/${productId}/start-sale/`, data);
    },

    // End sale on product
    endProductSale: (productId: number) => {
        return apiClient.post(`/vendors/products/${productId}/end-sale/`, {});
    },

    // Start sale on variant
    startVariantSale: (productId: number, variantId: number, data: { discount_percent: number; sale_start: string; sale_expiry: string }) => {
        return apiClient.post(`/vendors/products/${productId}/start-sale/`, { ...data, variant_id: variantId });
    },

    // End sale on variant
    endVariantSale: (productId: number, variantId: number) => {
        return apiClient.post(`/vendors/products/${productId}/end-sale/`, { variant_id: variantId });
    },

    // Vendor Addresses (using core addresses endpoint)
    // getVendorAddresses: async () => {
    //     const response = await apiClient.get('/addresses/');
    //     return response.data;
    // },
    // Updated to use StoreAddressSerializer instead

    /* Pickup Address Endpoints */
    getVendorAddresses: async () => {
        const response = await apiClient.get('vendors/store-addresses/');
        return response.data;
    },

    addVendorAddress: async (data: any) => {
        const response = await apiClient.post('/vendors/store-addresses/', data);
        return response.data;
    },

    updateVendorAddress: async (id: number, data: any) => {
        const response = await apiClient.put(`/vendors/store-addresses/${id}/`, data);
        return response.data;
    },

    deleteVendorAddress: async (id: number) => {
        const response = await apiClient.delete(`/vendors/store-addresses/${id}/`);
        return response.data;
    },

    updateStoreAddressCoordinates: async (storeAddressId: number, coordinates: { latitude: number; longitude: number }) => {
        const response = await apiClient.post(
            `/vendors/store-addresses/${storeAddressId}/update-coordinates/`,
            coordinates
        );
        return response.data;
    },

    // addVendorAddress: async (data: any) => {
    //     const response = await apiClient.post('/addresses/', data);
    //     return response.data;
    // },

    // updateVendorAddress: async (id: number, data: any) => {
    //     const response = await apiClient.put(`/addresses/${id}/`, data);
    //     return response.data;
    // },

    // deleteVendorAddress: async (id: number) => {
    //     const response = await apiClient.delete(`/addresses/${id}/`);
    //     return response.data;
    // },

    async setDefaultVendorAddress(id: number): Promise<ApiResponse<Address>> {
        try {
            const response = await apiClient.patch<Address>(`/addresses/${id}/set_default/`);
            return { data: response.data, status: response.status };
        } catch (error: any) {
            return {
                error: error.response?.data?.detail || 'Failed to set default address',
                status: error.response?.status || 500,
            };
        }
    },

    async updateAddressCoordinates(
        addressId: number,
        coordinates: { latitude: number; longitude: number }
    ): Promise<ApiResponse<{
        message: string;
        coordinates: { latitude: number; longitude: number };
        saved_at: string;
    }>> {
        try {
            // coordinates.longitude &&= formatCoord(coordinates.longitude);
            // coordinates.latitude &&= formatCoord(coordinates.latitude);
            const response = await apiClient.post(
                `/addresses/${addressId}/update_coordinates/`,
                coordinates
            );
            return { data: response.data, status: response.status };
        } catch (error: any) {
            return {
                error: error.response?.data?.error || 'Failed to update coordinates',
                status: error.response?.status || 500,
            };
        }
    },

    /**
     * Get vendor products for pickup selection
     * Returns approved, in-stock products sorted by lowest stock first
     */
    getProductsForPickup: async (params?: {
        search?: string;
        limit?: number;
        order_by?: string;
    }) => {
        const response = await apiClient.get('/vendors/products/for-pickup/', { params });
        return response;
    },

    getVendorStore: async () => {
        const response = await apiClient.get('/vendors/stores/store/');
        return response.data;
    },

    getVendorStores: async () => {
        const response = await apiClient.get('/vendors/stores/');
        return response.data;
    },

    createStore: async (data: any) => {
        const isFormData = data instanceof FormData;
        const response = await apiClient.post('/vendors/stores/', data, {
            headers: isFormData ? { 'Content-Type': 'multipart/form-data' } : undefined,
        });
        return response.data;
    },

    updateStore: async (id: number, data: any) => {
        const isFormData = data instanceof FormData;
        const response = await apiClient.put(`/vendors/stores/${id}/`, data, {
            headers: isFormData ? { 'Content-Type': 'multipart/form-data' } : undefined,
        });
        return response.data;
    },

    requestVendorTypeChange: async (data: {
        requested_type: string;
        reason: string;
        documents?: File[]
    }) => {
        const formData = new FormData();
        formData.append('requested_type', data.requested_type);
        formData.append('reason', data.reason);

        // Append each file
        if (data.documents) {
            data.documents.forEach((doc, index) => {
                formData.append(`documents[${index}]`, doc);
            });
        }

        const response = await apiClient.post('/vendors/request-business-type-change/', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response.data;
    },
    getReturns: async (params?: { status?: string }) => {
        const res = await apiClient.get('/vendors/returns/', { params });
        return res.data;
    },
    getReturn: async (id: string) => {
        const res = await apiClient.get<ReturnRequest>(`/vendors/returns/${id}/`);
        return res.data;
    },
    decideReturn: async (
        id: string,
        payload: {
            decision: 'approve' | 'partial' | 'reject';
            approved_quantity?: number;
            notes?: string;
        }
    ) => {
        const res = await apiClient.post<ReturnRequest>(
            `/vendors/returns/${id}/decide/`,
            payload
        );
        return res.data;
    },
};